import { useState } from "react";
import type { MultiplyConfig, SliceConfig } from "../../../types";
import styles from "./GridConfigDialog.module.css";
import {
  DIM_STEP,
  GUTTER_STEP,
  MAX_DIM,
  MAX_GUTTER,
  MIN_DIM,
  MIN_GUTTER,
} from "../../../const";
import { clamp, round2 } from "../../../utils/formatUtils";

export type GridMode = "multiply" | "slice";

/** Superset of both configs — the parent passes it to the matching host op. */
export type GridConfig = MultiplyConfig & SliceConfig;

interface GridConfigDialogProps {
  readonly mode: GridMode;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onApply: (cfg: GridConfig) => void;
}

interface StepperProps {
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly disabled: boolean;
  readonly onChange: (v: number) => void;
}

function Stepper({ label, value, min, max, step, disabled, onChange }: StepperProps): React.ReactElement {
  const set = (v: number): void => onChange(clamp(round2(v), min, max));
  return (
    <div className={styles.cell}>
      <div className={styles.cellLabel}>{label}</div>
      <div className={styles.stepper}>
        <button
          type="button"
          disabled={disabled || value <= min}
          onClick={() => set(value - step)}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          aria-label={label}
          onChange={(e) => {
            const n = Number.parseFloat(e.target.value);
            if (!Number.isNaN(n)) set(n);
          }}
        />
        <button
          type="button"
          disabled={disabled || value >= max}
          onClick={() => set(value + step)}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * Config dialog for the Layout group's Multiply + Slice actions. Collects
 * rows / columns / gutters (+ Multiply's Same-Size & Add-Header options) with a
 * live grid preview, then hands the config to the parent to run the host op.
 */
export function GridConfigDialog({
  mode,
  busy,
  onCancel,
  onApply,
}: GridConfigDialogProps): React.ReactElement {
  const isMultiply = mode === "multiply";
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [rowGutter, setRowGutter] = useState(0.2);
  const [colGutter, setColGutter] = useState(0.2);
  const [sameSize, setSameSize] = useState(false);
  const [addHeader, setAddHeader] = useState(false);

  const apply = (): void => onApply({ rows, cols, rowGutter, colGutter, sameSize, addHeader });

  // Content-derived keys (row/column position) so the placeholder cells don't
  // key on their bare array index.
  const previewCells = Array.from(
    { length: rows * cols },
    (_, i) => `r${Math.floor(i / cols)}-c${i % cols}`,
  );

  return (
    <dialog
      open
      className={styles.overlay}
      aria-modal="true"
      aria-label={`${isMultiply ? "Multiply" : "Slice"} options`}
    >
      <div className={styles.dialog}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <rect x="2" y="2" width="6" height="6" rx="1" />
              <rect x="12" y="2" width="6" height="6" rx="1" opacity="0.4" />
              <rect x="2" y="12" width="6" height="6" rx="1" opacity="0.4" />
              <rect x="12" y="12" width="6" height="6" rx="1" opacity="0.4" />
            </svg>
            {isMultiply ? "Multiply" : "Slice"}
          </span>
          <button type="button" className={styles.close} aria-label="Close" onClick={onCancel}>
            &times;
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.previewLabel}>Visual Preview</div>
          <div className={styles.preview}>
            <div
              className={styles.previewGrid}
              style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
            >
              {previewCells.map((key) => (
                <div key={key} className={styles.previewCell} />
              ))}
            </div>
          </div>

          <div className={styles.propsLabel}>Properties</div>
          <div className={styles.row}>
            <Stepper
              label="Rows"
              value={rows}
              min={MIN_DIM}
              max={MAX_DIM}
              step={DIM_STEP}
              disabled={busy}
              onChange={setRows}
            />
            <Stepper
              label="Row Gutter (in)"
              value={rowGutter}
              min={MIN_GUTTER}
              max={MAX_GUTTER}
              step={GUTTER_STEP}
              disabled={busy}
              onChange={setRowGutter}
            />
          </div>
          <div className={styles.row}>
            <Stepper
              label="Columns"
              value={cols}
              min={MIN_DIM}
              max={MAX_DIM}
              step={DIM_STEP}
              disabled={busy}
              onChange={setCols}
            />
            <Stepper
              label="Column Gutter (in)"
              value={colGutter}
              min={MIN_GUTTER}
              max={MAX_GUTTER}
              step={GUTTER_STEP}
              disabled={busy}
              onChange={setColGutter}
            />
          </div>

          {isMultiply && (
            <div className={styles.opts}>
              <label>
                <input
                  type="checkbox"
                  checked={sameSize}
                  disabled={busy}
                  onChange={(e) => setSameSize(e.target.checked)}
                />
                <span>Make Same Size</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={addHeader}
                  disabled={busy}
                  onChange={(e) => setAddHeader(e.target.checked)}
                />
                <span>Add Header</span>
              </label>
            </div>
          )}

          <div className={styles.footer}>
            <button type="button" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button type="button" className={styles.primary} onClick={apply} disabled={busy}>
              {busy ? "Working…" : "OK"}
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}
